import { getMachinePortLayout, MACHINE_TYPES, TUNING } from './definitions.js'

const GRID_SIZE = 0.75
const GRID_RADIUS = Math.ceil(TUNING.walkRadius / GRID_SIZE)

export const isWalkable = (_state, _x, _y) =>
{
    if(Math.hypot(_x, _y) > TUNING.walkRadius)
        return false
    if(_state.bin.revealed && Math.hypot(_x - _state.bin.x, _y - _state.bin.y) < 1.45)
        return false
    for(const deposit of _state.deposits)
    {
        if(!deposit.revealed || deposit.occupied || deposit.remaining <= 0)
            continue
        const definition = MACHINE_TYPES[_state.activeSite?.machineType]
        if(definition?.depositId === deposit.id)
            continue
        const radius = deposit.tree ? 0.55 : 2.3
        if(Math.hypot(_x - deposit.x, _y - deposit.y) < radius + 0.36)
            return false
    }
    for(const machine of _state.machines)
    {
        if(Math.hypot(_x - machine.x, _y - machine.y) >= 1.4)
            continue
        const layout = getMachinePortLayout(machine.type)
        const ports = [...Object.values(layout.inputs), layout.fuel, layout.output].filter(Boolean)
        const atPort = ports.some((_port) => Math.hypot(_x - machine.x - _port.x, _y - machine.y - _port.y) < 0.42)
        if(!atPort)
            return false
    }
    return true
}

const nodeKey = (_x, _y) => `${_x},${_y}`
const toWorld = (_node) => ({ x: _node.x * GRID_SIZE, y: _node.y * GRID_SIZE })
const toNode = (_x, _y) => ({ x: Math.round(_x / GRID_SIZE), y: Math.round(_y / GRID_SIZE) })
const isWalkableSegment = (_state, _startX, _startY, _endX, _endY) =>
{
    const steps = Math.max(1, Math.ceil(Math.hypot(_endX - _startX, _endY - _startY) / 0.15))
    for(let index = 1; index <= steps; index++)
    {
        const progress = index / steps
        if(!isWalkable(_state, _startX + (_endX - _startX) * progress, _startY + (_endY - _startY) * progress))
            return false
    }
    return true
}

const nearestWalkable = (_state, _x, _y) =>
{
    const origin = toNode(_x, _y)
    for(let radius = 0; radius <= 5; radius++)
    {
        let best = null
        let bestDistance = Infinity
        for(let x = origin.x - radius; x <= origin.x + radius; x++)
        {
            for(let y = origin.y - radius; y <= origin.y + radius; y++)
            {
                if(Math.max(Math.abs(x - origin.x), Math.abs(y - origin.y)) !== radius || Math.abs(x) > GRID_RADIUS || Math.abs(y) > GRID_RADIUS)
                    continue
                const position = toWorld({ x, y })
                const distance = Math.hypot(position.x - _x, position.y - _y)
                if(distance < bestDistance && isWalkable(_state, position.x, position.y) && isWalkableSegment(_state, _x, _y, position.x, position.y))
                {
                    best = { x, y }
                    bestDistance = distance
                }
            }
        }
        if(best)
            return best
    }
    return null
}

export const findPath = (_state, _startX, _startY, _targetX, _targetY) =>
{
    const start = nearestWalkable(_state, _startX, _startY)
    const goal = nearestWalkable(_state, _targetX, _targetY)
    if(!start || !goal)
        return []
    const startKey = nodeKey(start.x, start.y)
    const goalKey = nodeKey(goal.x, goal.y)
    if(startKey === goalKey)
    {
        const startWorld = toWorld(start)
        const path = Math.hypot(startWorld.x - _startX, startWorld.y - _startY) > 0.0001 ? [startWorld] : []
        path.push({ x: _targetX, y: _targetY })
        return path
    }

    const open = [start]
    const openKeys = new Set([startKey])
    const closed = new Set()
    const cameFrom = new Map()
    const costs = new Map([[startKey, 0]])
    const estimates = new Map([[startKey, Math.hypot(goal.x - start.x, goal.y - start.y)]])
    const directions = [
        [-1, 0, 1], [1, 0, 1], [0, -1, 1], [0, 1, 1],
        [-1, -1, Math.SQRT2], [-1, 1, Math.SQRT2], [1, -1, Math.SQRT2], [1, 1, Math.SQRT2]
    ]

    while(open.length)
    {
        let bestIndex = 0
        for(let index = 1; index < open.length; index++)
        {
            if((estimates.get(nodeKey(open[index].x, open[index].y)) ?? Infinity) < (estimates.get(nodeKey(open[bestIndex].x, open[bestIndex].y)) ?? Infinity))
                bestIndex = index
        }
        const current = open.splice(bestIndex, 1)[0]
        const currentKey = nodeKey(current.x, current.y)
        openKeys.delete(currentKey)
        if(currentKey === goalKey)
        {
            const nodes = [current]
            let cursor = currentKey
            while(cameFrom.has(cursor))
            {
                const previous = cameFrom.get(cursor)
                nodes.push(previous)
                cursor = nodeKey(previous.x, previous.y)
            }
            nodes.reverse()
            const path = nodes.map(toWorld)
            if(Math.hypot(path[0].x - _startX, path[0].y - _startY) < 0.0001)
                path.shift()
            path.push({ x: _targetX, y: _targetY })
            return path
        }
        closed.add(currentKey)

        for(const [offsetX, offsetY, cost] of directions)
        {
            const next = { x: current.x + offsetX, y: current.y + offsetY }
            if(Math.abs(next.x) > GRID_RADIUS || Math.abs(next.y) > GRID_RADIUS)
                continue
            const nextKey = nodeKey(next.x, next.y)
            if(closed.has(nextKey))
                continue
            const world = toWorld(next)
            if(!isWalkable(_state, world.x, world.y))
                continue
            const currentWorld = toWorld(current)
            if(!isWalkableSegment(_state, currentWorld.x, currentWorld.y, world.x, world.y))
                continue
            if(offsetX !== 0 && offsetY !== 0)
            {
                const horizontal = toWorld({ x: current.x + offsetX, y: current.y })
                const vertical = toWorld({ x: current.x, y: current.y + offsetY })
                if(!isWalkable(_state, horizontal.x, horizontal.y) || !isWalkable(_state, vertical.x, vertical.y))
                    continue
            }
            const nextCost = (costs.get(currentKey) ?? Infinity) + cost
            if(nextCost >= (costs.get(nextKey) ?? Infinity))
                continue
            cameFrom.set(nextKey, current)
            costs.set(nextKey, nextCost)
            estimates.set(nextKey, nextCost + Math.hypot(goal.x - next.x, goal.y - next.y))
            if(!openKeys.has(nextKey))
            {
                open.push(next)
                openKeys.add(nextKey)
            }
        }
    }
    return []
}
